import React from 'react'
import MediaQuery from 'react-responsive'

import { AcornsIcon } from '@acorns/icons'
import styled from 'styled-components'

import withdrawLogo from 'assets/images/withdraw-zero-balance.png'
import ConfirmationModal, { ModalAction } from 'components/ConfirmationModal'
import useWindowDimensions from 'hooks/useWindowDimensions'
import {
  RedStyledCloseAccountButton,
  StyledBody,
  StyledCard,
  StyledImage,
} from 'pages/close-account-v3/components'
import themeValues, { style } from 'theme'
import { USDFormatter } from 'utils/number/currency'

import { MergedProps } from './index'

const StyledRedButton = styled(RedStyledCloseAccountButton)`
  width: auto;
  @media (max-width: ${style((theme) => theme.breakpoints.sm - 1)}px) {
    width: 100% !important;
    margin-top: auto;
  }
`

const WVCard = styled(StyledCard)<{ innerHeight?: number }>`
  display: flex;
  justify-content: space-between;
  height: ${(props) => `calc(${props.innerHeight}px - 80px)`};
  &&& {
    border-radius: 0;
  }
`

const BodyContainer = styled.div`
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    height: auto;
    padding-top: 50px;
  }
  @media screen and (max-width: 375px) {
    padding-top: 10px;
  }
`

const ProcessingWithdrawls = styled.div`
  max-width: 500px;
  min-width: 400px;
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    min-width: 200px;
  }
  margin: 0 auto;
`

const Item = styled.div`
  border: 1px solid ${style((theme) => theme.colors.ivory)};
  &:first-child {
    border-top-left-radius: ${style((theme) => theme.styles.borderRadius)};
    border-top-right-radius: ${style((theme) => theme.styles.borderRadius)};
  }
  &:not(:first-child):not(:last-child) {
    border-top: none;
  }
  &:last-child:not(:first-child) {
    border-top: none;
    border-bottom-left-radius: ${style((theme) => theme.styles.borderRadius)};
    border-bottom-right-radius: ${style((theme) => theme.styles.borderRadius)};
  }
  &:last-child:first-child {
    border-bottom-left-radius: ${style((theme) => theme.styles.borderRadius)};
    border-bottom-right-radius: ${style((theme) => theme.styles.borderRadius)};
  }
  margin: 0 auto;
  height: 60px;

  padding: 10px 20px;
  align-items: center;
  justify-content: space-between;
  display: flex;
`

const StyledIcon = styled.div`
  float: left;
  margin-top: 4px;
  max-width: 24px;
  max-height: 24px;
`
const Product = styled.span`
  display: inline-block;
  vertical-align: middle;
  margin-top: 5px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0px;
  line-height: 22px;
  color: ${style((theme) => theme.colors.slate)};
  margin-left: 12px;
`

const Amount = styled.span`
  margin-top: 5px;
  font-size: 14px;
  font-weight: normal;
  letter-spacing: 0px;
  line-height: 22px;
  text-align: right;
  color: ${style((theme) => theme.colors.slate)};
  float: right;
`
const WrappingDiv = styled.div`
  display: inline-block;
  align-items: flex-start;
`
const ButtonContainer = styled.div`
  border-top: 1px solid ${style((theme) => theme.colors.ivory)};
  margin-top: 30px;
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    border-top: none;
    position: relative;
    width: 100%;
  }
  @media screen and (max-width: 321px) {
    margin-top: 5px;
  }
`

const Footnote = styled.div`
  margin-top: 30px;
  font-size: 12px;
  font-weight: normal;
  letter-spacing: 0px;
  line-height: 16px;
  text-align: center;
  margin-bottom: 50px;
  color: ${style((theme) => theme.colors.stone)};
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    margin-top: 0;
    margin-bottom: 20px;
  }
`
const renderIcon = (product: string) => {
  if (product === 'Spend') {
    return <AcornsIcon icon={AcornsIcon.Icon.Spend} width="24px" />
  } else if (product === 'Later') {
    return <AcornsIcon icon={AcornsIcon.Icon.Later} width="24px" />
  } else {
    return <AcornsIcon icon={AcornsIcon.Icon.Invest} width="24px" />
  }
}
const renderBalances = (balances: { product: string; amount: number }[]) => {
  return balances.map((item) => (
    <Item key={item.product}>
      <WrappingDiv>
        <StyledIcon>{renderIcon(item.product)}</StyledIcon>
        <Product>{item.product}</Product>
      </WrappingDiv>

      <Amount data-testid={`${item.product}_balance`}>
        {USDFormatter.format(item.amount)}
      </Amount>
    </Item>
  ))
}
type ModalProps = {
  modalHeader: string
  modalBody: string
  modalActions: ModalAction[]
}

type Props = Pick<
  MergedProps,
  | 'handleShowConfirmAccountClosureModal'
  | 'shouldShowCloseAccountConfirmationModal'
  | 'setShouldShowCloseAccountConfirmationModal'
  | 'userData'
  | 'loading'
  | 'balances'
  | 'hasBalance'
  | 'isWebView'
  | 'recurringInvestments'
  | 'handleCancelSubscriptionCTAPress'
> &
  ModalProps

type CustomCardProps = {
  isWebView: boolean
  innerHeight?: number | undefined
}

export const RequestScreen: React.FC<Props> = ({
  handleShowConfirmAccountClosureModal,
  modalActions,
  modalBody,
  modalHeader,
  setShouldShowCloseAccountConfirmationModal,
  shouldShowCloseAccountConfirmationModal,
  userData,
  loading,
  balances,
  hasBalance,
  isWebView,
}) => {
  const CardComponent = isWebView ? WVCard : StyledCard
  const { height } = useWindowDimensions()

  const cardProps: CustomCardProps = {
    isWebView,
    innerHeight: undefined,
  }

  if (isWebView) {
    cardProps.innerHeight = height
  }
  return (
    <>
      <CardComponent {...cardProps}>
        <BodyContainer>
          <StyledImage src={withdrawLogo} />

          {hasBalance ? (
            <StyledBody>
              Your money will be transferred to your{' '}
              <b>
                {userData?.fundingSource?.name} {userData?.fundingSource?.last4}
              </b>{' '}
              account, then we’ll automatically close your accounts and change
              your subscription.
            </StyledBody>
          ) : (
            <StyledBody>
              We'll automatically close the accounts and change your
              subscription.
            </StyledBody>
          )}
          <ProcessingWithdrawls>
            {balances && renderBalances(balances)}
          </ProcessingWithdrawls>
          {hasBalance && (
            <MediaQuery minWidth={themeValues.breakpoints.sm}>
              <Footnote>
                Transfers may take up to 3-6 business days to settle after your
                shares have been sold.
              </Footnote>
            </MediaQuery>
          )}
        </BodyContainer>
        <ButtonContainer>
          {hasBalance && (
            <MediaQuery maxWidth={themeValues.breakpoints.sm - 1}>
              <Footnote>
                Transfers may take up to 3-6 business days to settle after your
                shares have been sold.
              </Footnote>
            </MediaQuery>
          )}
          <StyledRedButton
            onPress={handleShowConfirmAccountClosureModal}
            disabled={loading}
          >
            {hasBalance ? 'Withdraw and request closure' : 'Request closure'}
          </StyledRedButton>
        </ButtonContainer>
      </CardComponent>

      <ConfirmationModal
        actions={modalActions}
        body={modalBody}
        header={modalHeader}
        onRequestClose={() => setShouldShowCloseAccountConfirmationModal(false)}
        isOpen={shouldShowCloseAccountConfirmationModal}
      />
    </>
  )
}
